import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE_URL, person, summary } from "../data/profile";
import { buildGraph } from "./structuredData";

/**
 * Seo — one component that owns the entire head for a route.
 *
 * Keyword stuffing is deliberately absent. Modern ranking and, more to the
 * point, LLM retrieval reward a page whose title, description, headings and
 * structured data all describe the same entity in plain language. The old
 * 40-term `keywords` meta was doing nothing but diluting that signal.
 */
export default function Seo({
  title,
  description = summary,
  path = "/",
  image = `${SITE_URL}/og-main.jpg`,
  type = "profile",
  noindex = false,
  graph,
  extraGraph = [],
  publishedAt,
  modifiedAt,
}) {
  const url = `${SITE_URL}${path === "/" ? "/" : path}`;
  const fullTitle =
    title || `${person.name} — ${person.headline} | Portfolio`;

  const jsonLd = graph || buildGraph({ url, extra: extraGraph });

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      <meta name="author" content={person.name} />
      <meta
        name="robots"
        content={
          noindex
            ? "noindex, nofollow"
            : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        }
      />
      {/* Explicitly welcome AI crawlers — the GEO equivalent of robots.txt.
          Being cited by an answer engine is the goal, not a leak. */}
      <meta name="googlebot" content="index, follow, max-snippet:-1" />
      <meta name="ai-content-declaration" content="human-authored" />

      {/* GEO: location qualifiers so "developer near Rohtak / Haryana"
          style queries can bind to this entity. */}
      <meta name="geo.region" content="IN-HR" />
      <meta name="geo.placename" content={`${person.location.city}, ${person.location.region}`} />
      <meta
        name="geo.position"
        content={`${person.location.latitude};${person.location.longitude}`}
      />
      <meta
        name="ICBM"
        content={`${person.location.latitude}, ${person.location.longitude}`}
      />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="en_IN" />
      <meta property="og:site_name" content={`${person.name} — Portfolio`} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content={`${person.name} — ${person.headline}`}
      />
      {type === "profile" && (
        <meta property="profile:first_name" content={person.givenName} />
      )}
      {type === "profile" && (
        <meta property="profile:last_name" content={person.familyName} />
      )}
      {publishedAt && (
        <meta property="article:published_time" content={publishedAt} />
      )}
      {modifiedAt && (
        <meta property="article:modified_time" content={modifiedAt} />
      )}

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta
        name="twitter:image:alt"
        content={`${person.name} — ${person.headline}`}
      />

      {/* Discovery */}
      <link rel="me" href={person.social.linkedin} />
      <link rel="me" href={person.social.github} />
      <link rel="alternate" type="application/xml" href={`${SITE_URL}/sitemap.xml`} />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  );
}
